import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Bell, CheckCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAppNotifications } from '@/contexts/NotificationsContext';
import { NotificationList } from './NotificationList';
import { cn } from '@/lib/utils';

type FilterTab = 'all' | 'unread';

function formatBadgeCount(count: number) {
  if (count <= 0) return null;
  if (count > 9) return '9+';
  return String(count);
}

export function NotificationsHub() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<FilterTab>('all');
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useAppNotifications();

  const badgeLabel = formatBadgeCount(unreadCount);

  const visibleNotifications = useMemo(() => {
    const source =
      filter === 'unread'
        ? notifications.filter((item) => item.readAt === null)
        : notifications;
    return source.slice(0, 40);
  }, [filter, notifications]);

  const handleItemClick = (id: string, contactId?: string | null) => {
    void markAsRead(id);
    if (contactId) {
      setOpen(false);
      navigate(`/crm/${contactId}`);
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex h-10 w-10 items-center justify-center rounded-xl text-[#475569] transition-colors hover:bg-[#F1F5F9] hover:text-[#0F172A] dark:text-[#A1A1AA] dark:hover:bg-[#3F3F46] dark:hover:text-[#FAFAFA]"
          aria-label={
            unreadCount > 0
              ? `Notificações, ${unreadCount} não lidas`
              : 'Notificações'
          }
        >
          <Bell className="h-5 w-5" />
          {badgeLabel && (
            <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#EF4444] px-1 text-[10px] font-bold leading-none text-white">
              {badgeLabel}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(100vw-1.5rem,400px)] overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white p-0 shadow-lg dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#E5E7EB] px-4 py-3.5 dark:border-[#3F3F464D]">
          <h2 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
            Notificações
          </h2>
          <button
            type="button"
            onClick={() => void markAllAsRead()}
            disabled={unreadCount === 0}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2563EB] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40 dark:text-[#60A5FA]"
          >
            <CheckCheck className="h-4 w-4" />
            Marcar todas como lidas
          </button>
        </div>

        <div className="flex gap-2 px-4 py-3">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={cn(
              'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              filter === 'all'
                ? 'bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]'
                : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] dark:bg-[#3F3F46] dark:text-[#A1A1AA] dark:hover:bg-[#52525B]'
            )}
          >
            Tudo
          </button>
          <button
            type="button"
            onClick={() => setFilter('unread')}
            className={cn(
              'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              filter === 'unread'
                ? 'bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]'
                : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] dark:bg-[#3F3F46] dark:text-[#A1A1AA] dark:hover:bg-[#52525B]'
            )}
          >
            Não lidas{unreadCount > 0 ? ` (${unreadCount})` : ''}
          </button>
        </div>

        <div className="max-h-[420px] overflow-y-auto border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
          <NotificationList
            notifications={visibleNotifications}
            onItemClick={handleItemClick}
            emptyMessage={
              filter === 'unread'
                ? 'Você não tem notificações não lidas.'
                : 'Nenhuma notificação por aqui.'
            }
          />
        </div>

        <div className="border-t border-[#E5E7EB] px-4 py-3 dark:border-[#3F3F464D]">
          <button
            type="button"
            onClick={handleViewAll}
            className="mx-auto flex items-center gap-1.5 text-sm font-medium text-[#2563EB] transition-opacity hover:opacity-80 dark:text-[#60A5FA]"
          >
            Ver todas as notificações
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationsHub;
